import { useGlobalStore } from "@/frontend/store/GlobalStore";
import { useSampleMaturityStore } from "@/frontend/store/SampleMaturityStore";
import { useRequirementsStore } from "@/frontend/store/RequirementsStore";
import { I18n } from "@/i18n/I18n";
import { useEffect, useState } from "react";
import { Badge, Button, Placeholder } from "react-bootstrap";
import { useShallow } from "zustand/react/shallow";

interface RequirementSampleMaturityAssignmentsProps {
  requirementId: string;
  projectId: string;
}

export function RequirementSampleMaturityAssignments({ requirementId, projectId }: RequirementSampleMaturityAssignmentsProps) {
  const sampleMaturities = useSampleMaturityStore(state => state.sampleMaturities);
  const fetchSampleMaturitiesByProjectId = useSampleMaturityStore(state => state.fetchSampleMaturitiesByProjectId);
  const assignments = useRequirementsStore(useShallow(state => state.requirementSampleMaturityAssignments.filter(a => a.requirementId === requirementId)));
  const fetchRequirementSampleMaturityAssignments = useRequirementsStore(state => state.fetchRequirementSampleMaturityAssignments);
  const createRequirementSampleMaturityAssignment = useRequirementsStore(state => state.createRequirementSampleMaturityAssignment);
  const deleteRequirementSampleMaturityAssignment = useRequirementsStore(state => state.deleteRequirementSampleMaturityAssignment);
  const [loading, setLoading] = useState(true);
  const userClass = useGlobalStore(state => state.userClass);

  useEffect(() => {
    void fetchSampleMaturitiesByProjectId(projectId);
  }, [projectId, fetchSampleMaturitiesByProjectId]);

  useEffect(() => {
    void fetchRequirementSampleMaturityAssignments(requirementId).finally(() => {
      setLoading(false);
    });
  }, [requirementId, fetchRequirementSampleMaturityAssignments]);

  const assignedSampleMaturityIds = assignments.map(a => a.sampleMaturityId);

  const handleToggle = async (sampleMaturityId: string) => {
    const isSelected = assignedSampleMaturityIds.includes(sampleMaturityId);

    if (isSelected) {
      const assignment = assignments.find(a => a.sampleMaturityId === sampleMaturityId);
      if (assignment) {
        await deleteRequirementSampleMaturityAssignment(requirementId, assignment.id);
      }
    } else {
      await createRequirementSampleMaturityAssignment(requirementId, sampleMaturityId);
    }
  };

  if (loading) {
    return <RequirementSampleMaturityAssignmentsPlaceholder />;
  }

  const viewOnly = sampleMaturities.some(sm => sm.propelOrgId !== userClass?.getOrgId());

  if ((viewOnly && assignedSampleMaturityIds.length === 0) || sampleMaturities.length === 0) {
    return null;
  }

  return (
    <div className="mt-2">
      {viewOnly && <div className="form-label-neutral">{I18n.get('samplePhases')}</div>}
      {!viewOnly && <div className="form-label-neutral">{I18n.get('samplePhasesDescription')}</div>}
      <div className="mt-1 d-flex gap-2 flex-wrap">
        {sampleMaturities.map(sampleMaturity => {
          const isSelected = assignedSampleMaturityIds.includes(sampleMaturity.id);

          if(!viewOnly) {
            return (
              <Button
                key={sampleMaturity.id}
                className={`fusa-btn-small ${isSelected ? 'btn-neutral-dark' : 'btn-neutral-middle'}`}
                onClick={() => void handleToggle(sampleMaturity.id)}
                aria-pressed={isSelected}
              >
                {sampleMaturity.name}
              </Button>
            );
          }

          if (isSelected) {
            return <Badge key={sampleMaturity.id} className="badge text-bg-dark" bg="dark">{sampleMaturity.name}</Badge>;
          }
        })}
      </div>
    </div>
  );
}

function RequirementSampleMaturityAssignmentsPlaceholder() {
  return (
    <div className="mt-3">
      <h5 className="text-muted fw-bold m-0">{I18n.get('samplePhases')}</h5>
      <small className="text-muted mb-0">{I18n.get('samplePhasesDescription')}</small>
      <Placeholder data-testid="loadingIndicator" className='d-flex gap-2 pt-3 observed-element' animation='glow'>
        <Placeholder.Button xs={1} size='xs' className='btn btn-secondary' />
        <Placeholder.Button xs={1} size='xs' className='btn btn-secondary' />
        <Placeholder.Button xs={1} size='xs' className='btn btn-secondary' />
      </Placeholder>
    </div>
  );
}
